import _ from 'lodash'
import { CoTrustedPartner, GoldenTicketType, ITimeLineConfig, NftRarityArgs } from 'Modules'

import { PHASE_ITEMS } from 'constants/pto-v2'

/**
 * Clean metadata object - remove empty values
 */
export const cleanMetadata = (metadata: any) => {
  if (_.isNil(metadata)) return null

  if (!_.isPlainObject(metadata)) return null

  const cleaned = _.pickBy(metadata, (v) => {
    if (_.isArray(v)) return v.length > 0
    if (_.isObject(v)) return !_.isEmpty(v)
    return !_.isNil(v) && v !== ''
  })

  return _.isEmpty(cleaned) ? null : cleaned
}

/**
 * Clean golden tickets - filter out empty items
 * @param goldenTickets - array of specials from form
 */
export const cleanGoldenTickets = (goldenTickets: any[]) => {
  if (!goldenTickets || goldenTickets.length === 0) return []

  return goldenTickets
    .filter((item) => {
      const hasName = item.name && item.name.trim() !== ''
      const hasDescription = item.description && item.description.trim() !== ''
      const hasQuantity = item.quantity && item.quantity > 0
      const hasImage = item.image && item.image.trim() !== ''
      return hasName || hasDescription || hasQuantity || hasImage
    })
    ?.map(({ isNewRecord: _, rarityId: __, ...item }) => ({
      ...item,
    }))
}

/**
 * Clean co-trusted partners - trim values and filter out empty items
 * @param partners - array of partners from form
 */
export const cleanCoTrustedPartners = (partners: any[]) => {
  if (!partners || partners.length === 0) return []

  return partners
    .map((item) => ({
      name: (item?.name || '').trim(),
      logo: (item?.logo || '').trim(),
      website: (item?.website || '').trim(),
    }))
    .filter((item) => item.name || item.logo || item.website)
}

/**
 * Clean phases for PTO v2 (createPool) - tier is removed from the contract,
 * maxNFTsWhitelisted equals maxNFTs for the whitelist phase and 0 elsewhere.
 */
export const cleanPhasesV2 = (phases: ITimeLineConfig[]) => {
  const enabledPhases = (phases || []).filter((phase) => phase.enabled)

  return enabledPhases.map((phase) => {
    const phaseAny = phase as any
    const maxNFTs = phase.isPurchasePhase ? Number(phaseAny.maxNFTs || 0) : 0

    return {
      name: phase.name,
      startTime: phase.startTime || 0,
      endTime: phase.isEndTimeUnlimited ? 0 : phase.endTime || 0,
      isEndTimeUnlimited: !!phase.isEndTimeUnlimited,
      isPurchasePhase: !!phase.isPurchasePhase,
      isClaimPhase: phaseAny.isClaimPhase || false,
      maxNFTs,
      maxNFTsWhitelisted: phase.name !== 'whitelist' ? 0 : maxNFTs,
      nftsReserved: 0,
    }
  })
}

/**
 * Map golden tickets to NFT rarities for createPool (rarityId is 1-based index).
 */
export const initNftRarities = (goldenTickets?: GoldenTicketType[]): NftRarityArgs[] => {
  if (!goldenTickets?.length) return []
  return goldenTickets
    .map((item, index) => ({
      rarityId: index + 1,
      count: Number(item.quantity || 0),
    }))
    .filter((item) => item.count > 0)
}

/**
 * Initialize timelines for create/list-for-sale mode
 */
export const initTimeLinesCreate = () => {
  return PHASE_ITEMS.map(
    (item) =>
      ({
        name: item.name,
        required: item.required || false,
        enabled: item.enabled || false,
        startTime: 0,
        endTime: 0,
        isEndTimeUnlimited: item.isEndTimeUnlimited,
        isPurchasePhase: item.isPurchasePhase,
        maxNFTs: 0,
        maxNFTsWhitelisted: 0,
        nftsReserved: 0,
        isClaimPhase: item.isClaimPhase || false,
      }) as ITimeLineConfig
  )
}

/**
 * Initialize timelines for edit mode with existing phases data
 */
export const initTimeLinesEdit = (phases?: ITimeLineConfig[]) => {
  return PHASE_ITEMS.map((item) => {
    const phase = phases?.find((phase) => phase.name === item.name)

    return {
      name: phase?.name || item.name,
      required: phase?.required || item.required || false,
      enabled: !!phase,
      startTime: phase?.startTime || 0,
      endTime: phase?.endTime || 0,
      isEndTimeUnlimited: phase?.isEndTimeUnlimited || item.isEndTimeUnlimited,
      isPurchasePhase: phase?.isPurchasePhase || item.isPurchasePhase,
      maxNFTs: phase?.maxNFTs || 0,
      maxNFTsWhitelisted: phase?.maxNFTsWhitelisted || 0,
      nftsReserved: phase?.nftsReserved || 0,
      isClaimPhase: phase?.isClaimPhase || false,
    } as ITimeLineConfig
  })
}

/**
 * Initialize tokenize specials
 */
export const INITIAL_GOLDEN_TICKET: GoldenTicketType = {
  name: '',
  description: '',
  quantity: null as unknown as number,
  image: '',
  rarityId: undefined,
  isNewRecord: true,
}

export const INITIAL_CO_TRUSTED_PARTNER: CoTrustedPartner = {
  name: '',
  logo: '',
  website: '',
}

/**
 * Normalize terms from API for form field (string).
 * Supports legacy plain string and current { content, flowType } shape.
 */
export const normalizeTermsContent = (terms: unknown): string => {
  if (terms == null) return ''
  if (typeof terms === 'string') return terms
  if (typeof terms === 'object' && terms !== null && 'content' in terms) {
    const content = (terms as { content?: unknown }).content
    return typeof content === 'string' ? content : ''
  }
  return ''
}
